import { otherColors } from '@ehrTheme/colors';
import { TabList } from '@mui/lab';
import { AppBar, Box, Container, Tab, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { ReactElement, SyntheticEvent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ProviderNotifications } from 'src/features';
import { RoleType } from 'utils';
import useEvolveUser from '../../hooks/useEvolveUser';
import { AppTab, useNavStore } from '../../state/nav.store';
import { LanguagePicker } from './LanguagePicker';
import MobileMenu from './MobileMenu';
import { UserMenu } from './UserMenu';

const { VITE_APP_ORGANIZATION_NAME_SHORT: ORGANIZATION_NAME_SHORT } = import.meta.env;
if (ORGANIZATION_NAME_SHORT == null) {
  throw new Error('Could not load env variable');
}

export type NavbarItems = {
  [key in AppTab]?: { urls: string[] };
};

const administratorNavbarItems: NavbarItems = {
  Home: { urls: ['/home'] },
  // Patients: { urls: ['/patients', '/patient'] },
  Telemedicine: { urls: ['/telemed/appointments', '/telemed', '/video-call'] },
  Admin: { urls: ['/telemed-admin'] },
};

const managerNavbarItems: NavbarItems = {
  Home: { urls: ['/home'] },
  // Patients: { urls: ['/patients', '/patient'] },
  Telemedicine: { urls: ['/telemed/appointments', '/telemed', '/video-call'] },
  Admin: { urls: ['/telemed-admin'] },
};

const staffNavbarItems: NavbarItems = {
  Home: { urls: ['/home'] },
  // Patients: { urls: ['/patients', '/patient'] },
  Telemedicine: { urls: ['/telemed/appointments', '/telemed', '/video-call'] },
};

const providerNavbarItems: NavbarItems = {
  Home: { urls: ['/home'] },
  // Patients: { urls: ['/patients', '/patient'] },
  Telemedicine: { urls: ['/telemed/appointments', '/telemed', '/video-call'] },
};

const customerSupportNavbarItems: NavbarItems = {
  Home: { urls: ['/home'] },
  // Patients: { urls: ['/patients', '/patient'] },
  Telemedicine: { urls: ['/telemed/appointments', '/telemed', '/video-call'] },
  Admin: { urls: ['/telemed-admin'] },
};

const hideNavbarPathPatterns = [/^\/telemed\/appointments\/(?!.*\/visit-details$)/];

export default function Navbar(): ReactElement | null {
  const location = useLocation();
  const currentTab = useNavStore((state) => state.currentTab);
  const user = useEvolveUser();
  const theme = useTheme();
  const { t } = useTranslation();

  const navbarItems: NavbarItems = useMemo(() => {
    let navItems = {};

    if (user) {
      if (user.hasRole([RoleType.Administrator])) {
        navItems = { ...navItems, ...administratorNavbarItems };
      }
      if (user.hasRole([RoleType.Manager])) {
        navItems = { ...navItems, ...managerNavbarItems };
      }
      if (user.hasRole([RoleType.Staff])) {
        navItems = { ...navItems, ...staffNavbarItems };
      }
      if (user.hasRole([RoleType.Provider])) {
        navItems = { ...navItems, ...providerNavbarItems };
      }
      if (user.hasRole([RoleType.CustomerSupport])) {
        navItems = { ...navItems, ...customerSupportNavbarItems };
      }
    }
    return navItems;
  }, [user]);

  // on page load set the tab to the opened page
  const currentUrl = '/' + location.pathname.substring(1).split('/')[0];

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!currentTab) {
      useNavStore.setState({ currentTab: 'Home' });
    }

    (Object.keys(navbarItems) as AppTab[]).forEach((navbarItem) => {
      if (navbarItems[navbarItem]!.urls.includes(currentUrl)) {
        useNavStore.setState({ currentTab: navbarItem });
      }
    });
  }, [currentTab, currentUrl, location.pathname, navbarItems]);

  if (hideNavbarPathPatterns.some((pattern) => pattern.test(location.pathname))) {
    return null;
  }

  return (
    <AppBar
      position="sticky"
      color="transparent"
      sx={{
        boxShadow: 'none',
        borderBottom: `1px solid ${otherColors.lightDivider}`,
        backgroundColor: theme.palette.background.paper,
        top: 0,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters variant="dense">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Box
              component="img"
              src="/Logo.png"
              alt="alton Med"
              sx={{
                height: { xs: 32, lg: 40 },
                mr: { xs: 0, lg: 4 },
                display: 'flex',
                alignItems: 'center',
              }}
            />
          </Link>
          {isMobile ? (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <LanguagePicker />
              {user?.hasRole([RoleType.Provider]) && <ProviderNotifications />}
              <UserMenu />
              <MobileMenu navbarItems={navbarItems} />
            </>
          ) : (
            <TabList
              onChange={(_: SyntheticEvent, value: string) => {
                useNavStore.setState({ currentTab: value });
              }}
              sx={{
                mt: 2.5,
                minHeight: 60,
                flexGrow: 1,
              }}
            >
              {currentTab &&
                (Object.keys(navbarItems) as AppTab[]).map((navbarItem, index) => (
                  <Tab
                    key={navbarItem}
                    label={
                      navbarItem === 'Home'
                        ? t('navbar.home')
                        : navbarItem === 'In Person'
                          ? t('navbar.inPerson')
                          : navbarItem === 'Schedules'
                            ? t('navbar.schedules')
                            : navbarItem === 'Patients'
                              ? t('navbar.patients')
                              : navbarItem === 'Employees'
                                ? t('navbar.employees')
                                : navbarItem === 'Telemedicine'
                                  ? t('navbar.telemedicine')
                                  : navbarItem === 'Tasks'
                                    ? t('navbar.tasks')
                                    : navbarItem === 'Admin'
                                      ? t('navbar.admin')
                                      : navbarItem
                    }
                    value={navbarItem}
                    id={`navbar-tab-${index}`}
                    aria-controls={`hello-${index}`} // `tabpanel-${index}`
                    component={Link}
                    to={navbarItems[navbarItem]!.urls?.[0]}
                    sx={{
                      fontSize: 16,
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  />
                ))}
            </TabList>
          )}

          {!isMobile && (
            <>
              <LanguagePicker />
              {user?.hasRole([RoleType.Provider]) && <ProviderNotifications />}
              <UserMenu />
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
}
